import { useEffect, useRef, useState } from "react";
import { Bell, CheckCheck } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications/me?limit=8");
      setData(data);
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };
  const openItem = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`);
    setOpen(false);
    load();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="notif-bell"
        aria-label="Notifications"
        className="relative rounded-full p-2 text-[color:var(--ink-muted)] transition-colors hover:bg-[color:var(--ink-hairline)] hover:text-[color:var(--ink)]"
      >
        <Bell size={16} />
        {data.unread > 0 && (
          <span
            data-testid="notif-unread"
            className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-[color:var(--flare,#e07a3f)] px-1 text-[9px] font-bold leading-none text-white"
          >
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notif-panel"
          className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-xl border border-[color:var(--ink-hairline)] bg-white shadow-xl"
        >
          <div className="flex items-center justify-between border-b border-[color:var(--ink-hairline)] px-4 py-3">
            <p className="text-sm font-semibold text-[color:var(--ink)]">Notifications</p>
            {data.unread > 0 && (
              <button
                onClick={markAll}
                data-testid="notif-mark-all"
                className="inline-flex items-center gap-1 text-xs font-medium text-[color:var(--evergreen)] hover:opacity-70"
              >
                <CheckCheck size={13} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {data.items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-[color:var(--ink-muted)]">
                You're all caught up.
              </p>
            ) : (
              data.items.map((n) => (
                <Link
                  to={n.product_slug ? `/product/${n.product_slug}` : "/dashboard"}
                  key={n.id}
                  onClick={() => openItem(n)}
                  data-testid={`notif-item-${n.id}`}
                  className={`block border-b border-[color:var(--ink-hairline)] px-4 py-3 last:border-0 transition-colors hover:bg-[color:var(--paper-soft)] ${
                    n.read ? "" : "bg-[color:var(--paper-soft)]"
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && (
                      <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[color:var(--evergreen)]" />
                    )}
                    <div className={n.read ? "opacity-70" : ""}>
                      <p className="text-sm font-medium text-[color:var(--ink)]">{n.title}</p>
                      <p className="mt-0.5 text-xs text-[color:var(--ink-body)]">{n.body}</p>
                      <p className="mt-1 text-[10px] text-[color:var(--ink-muted)]">
                        {new Date(n.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
